import React from 'react';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FieldLabel from '../components/FieldLabel';
import { getFieldLabel } from '../utils/formHelpers';
import { outlinedFieldProps } from '../utils/fieldStyles';

const CREATE_COMPONENT_VALUE = '__create_new_component__';
const SEARCH_EXISTING_VALUE = '__search_existing_component__';

const getComponentQuantityBounds = (component) => {
    if (!component) return { min: '', max: '' };
    if (component.min_occupancy != null || component.max_occupancy != null) {
        return { min: component.min_occupancy ?? '', max: component.max_occupancy ?? '' };
    }
    if (component.min_passenger != null || component.max_passenger != null) {
        return { min: component.min_passenger ?? '', max: component.max_passenger ?? '' };
    }
    if (component.minimum_participants != null || component.maximum_participants != null) {
        return {
            min: component.minimum_participants ?? '',
            max: component.maximum_participants ?? '',
        };
    }
    return { min: '', max: '' };
};

const ComponentSelectField = ({
    field,
    data,
    change,
    hasError,
    errorMessage,
    tripComponents = [],
    onNewComponent,
    onSearchExistingComponent,
    attachContext = null,
}) => {
    const handleComponentChange = (componentId) => {
        if (field.key === 'linked_component_id') {
            const selected = (tripComponents || []).find(
                (c) => String(c.id) === String(componentId)
            );
            const bounds = getComponentQuantityBounds(selected);
            change({
                linked_component_id: componentId,
                min_quantity: bounds.min,
                max_quantity: bounds.max,
            });
            return;
        }

        const selected = (tripComponents || []).find(
            (c) => String(c.id) === String(componentId)
        );
        change(field.key, componentId);
        if (field.key === 'trip_component_map' && selected?.label && !data.item_title) {
            const name = selected.label.replace(/\s*\([^)]*\)\s*$/, '').trim();
            if (name) change('item_title', name);
        }
    };

    const showSearchOption = typeof onSearchExistingComponent === 'function';
    const showCreateOption = typeof onNewComponent === 'function';

    return (
        <TextField
            {...outlinedFieldProps}
            select
            label={<FieldLabel label={getFieldLabel(field)} tooltip={field.tooltip} />}
            value={data[field.key] || ''}
            onChange={(e) => {
                const selectedValue = e.target.value;
                if (selectedValue === CREATE_COMPONENT_VALUE) {
                    onNewComponent?.((newComponentId) => handleComponentChange(newComponentId));
                    return;
                }
                if (selectedValue === SEARCH_EXISTING_VALUE) {
                    onSearchExistingComponent?.((newComponentId) => handleComponentChange(newComponentId), attachContext);
                    return;
                }
                handleComponentChange(selectedValue);
            }}
            error={hasError}
            helperText={hasError ? (errorMessage || 'This field is required') : ''}
        >
            {showSearchOption && (
                <MenuItem value={SEARCH_EXISTING_VALUE} sx={{ color: 'info.main', fontWeight: 600 }}>
                    <SearchIcon fontSize="small" sx={{ mr: 0.75 }} />
                    Search existing trips…
                </MenuItem>
            )}
            {(tripComponents || []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                    {c.label}
                </MenuItem>
            ))}
            {showCreateOption && (
                <MenuItem value={CREATE_COMPONENT_VALUE} sx={{ color: 'info.main', fontWeight: 600 }}>
                    <AddIcon fontSize="small" sx={{ mr: 0.75 }} />
                    Create new trip logistics
                </MenuItem>
            )}
        </TextField>
    );
};

export default ComponentSelectField;
